import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Hotel, MessageSquare, BookOpen, Users, TrendingUp, AlertCircle } from 'lucide-react';

function StatCard({ icon: Icon, label, value, color = '#3b82f6', sub }) {
  return (
    <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
      <div style={{ width: 48, height: 48, borderRadius: 12, background: color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon size={22} color={color} />
      </div>
      <div>
        <div style={{ fontSize: 26, fontWeight: 700, color: '#0f172a' }}>{value ?? '—'}</div>
        <div style={{ fontSize: 13, color: '#64748b' }}>{label}</div>
        {sub && <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{sub}</div>}
      </div>
    </div>
  );
}

export default function SuperAdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/superadmin/stats')
      .then(setStats)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ padding: 28, color: '#94a3b8' }}>Yükleniyor...</div>;
  if (!stats) return <div style={{ padding: 28, color: '#ef4444' }}>Veriler yüklenemedi</div>;

  return (
    <div style={{ padding: 28 }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#0f172a' }}>Platform Genel Bakış</h1>
        <p style={{ fontSize: 14, color: '#64748b', marginTop: 4 }}>Tüm oteller ve sistem durumu</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 28 }}>
        <StatCard icon={Hotel} label="Toplam Otel" value={stats.totalHotels} color="#3b82f6"
          sub={`${stats.activeHotels} aktif`} />
        <StatCard icon={MessageSquare} label="Toplam Konuşma" value={stats.totalConversations} color="#8b5cf6"
          sub={`${stats.recentConversations} bu hafta`} />
        <StatCard icon={BookOpen} label="Toplam Rezervasyon" value={stats.totalReservations} color="#10b981" />
        <StatCard icon={Users} label="Otel Kullanıcıları" value={stats.totalUsers} color="#f59e0b" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Plan Dağılımı */}
        <div className="card">
          <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Plan Dağılımı</h2>
          {stats.planStats.length === 0 && <div style={{ color: '#94a3b8', fontSize: 13 }}>Veri yok</div>}
          {stats.planStats.map(p => (
            <div key={p.plan} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: p.plan === 'PROFESSIONAL' ? '#3b82f6' : p.plan === 'ENTERPRISE' ? '#8b5cf6' : '#94a3b8' }} />
                <span style={{ fontSize: 14, fontWeight: 500 }}>{p.plan}</span>
              </div>
              <span style={{ fontSize: 18, fontWeight: 700, color: '#0f172a' }}>{p.count}</span>
            </div>
          ))}
        </div>

        {/* Hızlı Erişim */}
        <div className="card">
          <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Hızlı Erişim</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[
              { label: 'Tüm Otelleri Yönet', desc: `${stats.totalHotels} otel`, to: '/superadmin/hotels', color: '#3b82f6' },
              { label: 'Kullanıcıları Yönet', desc: `${stats.totalUsers} kullanıcı`, to: '/superadmin/users', color: '#8b5cf6' },
            ].map(item => (
              <button key={item.to} onClick={() => navigate(item.to)} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '12px 14px', border: '1px solid #e2e8f0', borderRadius: 8,
                background: 'white', cursor: 'pointer', textAlign: 'left', transition: 'border-color 0.15s',
              }}
                onMouseEnter={e => e.currentTarget.style.borderColor = item.color}
                onMouseLeave={e => e.currentTarget.style.borderColor = '#e2e8f0'}
              >
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{item.label}</div>
                  <div style={{ fontSize: 12, color: '#94a3b8' }}>{item.desc}</div>
                </div>
                <TrendingUp size={16} color={item.color} />
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
